import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Plus, Printer, ArrowClockwise, Trash, X, PackageDuotone, Eye, FilePdf, Camera, CalendarBlank, PencilSimple, CurrencyDollar,
} from "@phosphor-icons/react";
import SignaturePad from "@/components/SignaturePad";
import RecordDetailDialog from "@/components/RecordDetailDialog";
import SiteAutocomplete from "@/components/SiteAutocomplete";
import { fmtDate, fmtDateTime, fmtDateShort, nowStamp } from "@/lib/dates";
import StockPartCombobox from "@/components/StockPartCombobox";
import LockStatusBanner from "@/components/LockStatusBanner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

function KindBadge({ kind }) {
  const isRb = kind === "rb";
  return (
    <span
      data-testid={`kind-badge-${kind || "new"}`}
      className={`px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider ${isRb ? "bg-amber-100 text-amber-800" : "bg-neutral-900 text-neutral-50"}`}
      title={isRb ? "Repair Basis" : "New"}
    >
      {isRb ? "RB" : "NEW"}
    </span>
  );
}

function GroupBadge({ group }) {
  if (!group) return null;
  return (
    <span
      className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider bg-neutral-100 text-neutral-700 truncate max-w-[110px]"
      title={group}
    >
      {group}
    </span>
  );
}

export default function RecordsPage() {
  const { user } = useAuth();
  const canCreate = ["supervisor", "manager", "admin"].includes(user.role);
  const canDelete = user.role === "manager";
  const canEdit = user.role === "manager";
  const canReturn = ["supervisor", "manager", "admin", "storekeeper", "engineer"].includes(user.role);
  // Engineers can only submit returns for their OWN records — enforced on
  // the row level by comparing engineer_id below.
  const canReturnOnRecord = (r) => canReturn && (user.role !== "engineer" || r.engineer_id === user.id);

  const [records, setRecords] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [regions, setRegions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterRegion, setFilterRegion] = useState("all");
  const [filterStatus, setFilterStatus] = useState(() => {
    const s = (searchParams.get("status") || "all").toLowerCase();
    return ["open", "returned", "all"].includes(s) ? s : "all";
  });
  const [filterEngineer, setFilterEngineer] = useState(() => searchParams.get("engineer") || "all");
  const [filterCustomer, setFilterCustomer] = useState(() => searchParams.get("customer") || "all");
  const [filterWalkin, setFilterWalkin] = useState(() => {
    const w = (searchParams.get("walkin") || "all").toLowerCase();
    return ["all", "exclude", "only"].includes(w) ? w : "all";
  });
  const [filterPl, setFilterPl] = useState(() => {
    const p = (searchParams.get("pl") || "exclude").toLowerCase();
    return ["exclude", "include", "only"].includes(p) ? p : "exclude";
  });
  const [search, setSearch] = useState("");
  const [filterSite, setFilterSite] = useState(() => searchParams.get("site") || "");
  const [filterSerial, setFilterSerial] = useState(() => searchParams.get("serial") || "");
  // Default date range: last 90 days, overridable by URL (`?from=…&to=…`).
  const [dateFrom, setDateFrom] = useState(() => {
    const q = searchParams.get("from");
    if (q !== null) return q;
    const d = new Date(); d.setDate(d.getDate() - 90);
    return d.toISOString().slice(0, 10);
  });
  const [dateTo, setDateTo] = useState(() => searchParams.get("to") || "");
  const [loading, setLoading] = useState(true);
  // Server-side pagination
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  // When any Print button is clicked, we fetch the WHOLE filtered set (ignoring
  // page) into this buffer so the browser print covers everything — not just
  // the 25 rows currently on screen. Reset after window.print() returns.
  const [printRecords, setPrintRecords] = useState(null);

  // Keep the status filter in sync with the URL — the Issued/Returned tabs
  // navigate here with ?status=open or ?status=returned.
  useEffect(() => {
    const s = (searchParams.get("status") || "all").toLowerCase();
    const next = ["open", "returned", "all"].includes(s) ? s : "all";
    if (next !== filterStatus) setFilterStatus(next);
    const eng = searchParams.get("engineer") || "all";
    if (eng !== filterEngineer) setFilterEngineer(eng);
    const w = (searchParams.get("walkin") || "all").toLowerCase();
    const nextW = ["all", "exclude", "only"].includes(w) ? w : "all";
    if (nextW !== filterWalkin) setFilterWalkin(nextW);
    // eslint-disable-next-line
  }, [searchParams]);

  const changeWalkin = (v) => {
    setFilterWalkin(v);
    const p = new URLSearchParams(searchParams);
    if (v === "all") p.delete("walkin"); else p.set("walkin", v);
    setSearchParams(p, { replace: true });
  };
  const changePl = (v) => {
    setFilterPl(v);
    const p = new URLSearchParams(searchParams);
    if (v === "exclude") p.delete("pl"); else p.set("pl", v);
    setSearchParams(p, { replace: true });
  };

  const changeStatus = (v) => {
    setFilterStatus(v);
    const p = new URLSearchParams(searchParams);
    if (v === "all") p.delete("status"); else p.set("status", v);
    setSearchParams(p, { replace: true });
  };

  const [showCreate, setShowCreate] = useState(false);
  const [showReturn, setShowReturn] = useState(false);
  const [activeRecord, setActiveRecord] = useState(null);
  const [chargeEditing, setChargeEditing] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const downloadReceipt = async (id, copies = 1) => {
    try {
      const url = copies === 3 ? `/records/${id}/receipt.pdf?copies=3` : `/records/${id}/receipt.pdf`;
      const res = await api.get(url, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `receipt-${id.slice(0, 8)}${copies === 3 ? "-3ply" : ""}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, size: PAGE_SIZE };
      if (filterRegion !== "all") params.region = filterRegion;
      if (filterStatus !== "all") params.status = filterStatus;
      if (filterEngineer !== "all") params.engineer_id = filterEngineer;
      if (filterCustomer !== "all") params.customer_id = filterCustomer;
      if (filterWalkin !== "all") params.walkin = filterWalkin;
      if (filterPl !== "exclude") params.pl = filterPl;
      if (filterSerial.trim()) params.serial = filterSerial.trim();
      if (filterSite.trim()) params.site = filterSite.trim();
      if (search.trim()) params.q = search.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const [rec, eng, reg, cust] = await Promise.all([
        api.get("/records", { params }),
        user.role !== "engineer" ? api.get("/engineers") : Promise.resolve({ data: [] }),
        api.get("/regions"),
        user.role !== "engineer" ? api.get("/customers") : Promise.resolve({ data: [] }),
      ]);
      // Backend returns `{items,total,page,size}` when `page` is passed.
      const payload = rec.data;
      if (Array.isArray(payload)) {
        setRecords(payload);
        setTotal(payload.length);
      } else {
        setRecords(payload.items || []);
        setTotal(payload.total || 0);
      }
      setEngineers(eng.data);
      setRegions(reg.data);
      setCustomers(cust.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterRegion, filterStatus, filterEngineer, filterCustomer, filterWalkin, filterPl, filterSerial, filterSite, search, dateFrom, dateTo, page]);

  // Reset to page 1 whenever any filter / search term changes (except `page` itself).
  // eslint-disable-next-line
  useEffect(() => { setPage(1); }, [filterRegion, filterStatus, filterEngineer, filterCustomer, filterWalkin, filterPl, filterSerial, filterSite, search, dateFrom, dateTo]);

  // Deep-link: /records?open_return=<record_id> auto-opens the Submit Return
  // dialog for that record. Used by the engineer mobile dashboard's per-IV
  // "Submit Return" tap target.
  useEffect(() => {
    const rid = searchParams.get("open_return");
    if (!rid || !records.length) return;
    const rec = records.find((r) => r.id === rid);
    if (rec && canReturnOnRecord(rec) && rec.status === "open"
        && (rec.return_stage === "none" || !rec.return_stage)) {
      setActiveRecord(rec);
      setShowReturn(true);
    }
    // strip the param so a manual close doesn't re-open on re-render
    searchParams.delete("open_return");
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line
  }, [records]);

  // Server-side filters & pagination mean `records` is already the filtered page.
  // When a Print button is clicked we temporarily replace this with the full
  // filtered set (`printRecords`) so the printed table isn't just one page.
  const filtered = printRecords || records;

  const dateRangeLabel = useMemo(() => {
    if (!dateFrom && !dateTo) return "";
    const fmt = (d) => d ? fmtDateShort(d + "T00:00:00") : "…";
    return `${fmt(dateFrom)} → ${fmt(dateTo)}`;
  }, [dateFrom, dateTo]);

  const clearDateRange = () => {
    setDateFrom(""); setDateTo("");
    const p = new URLSearchParams(searchParams);
    p.delete("from"); p.delete("to");
    setSearchParams(p);
  };
  const commitDateRange = () => {
    const p = new URLSearchParams(searchParams);
    if (dateFrom) p.set("from", dateFrom); else p.delete("from");
    if (dateTo) p.set("to", dateTo); else p.delete("to");
    setSearchParams(p);
  };
  const applyPreset = (kind) => {
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    let from, to = iso(today);
    if (kind === "today") from = iso(today);
    else if (kind === "7d") { const d = new Date(today); d.setDate(d.getDate() - 6); from = iso(d); }
    else if (kind === "30d") { const d = new Date(today); d.setDate(d.getDate() - 29); from = iso(d); }
    else if (kind === "mtd") { const d = new Date(today.getFullYear(), today.getMonth(), 1); from = iso(d); }
    else if (kind === "ytd") { const d = new Date(today.getFullYear(), 0, 1); from = iso(d); }
    setDateFrom(from); setDateTo(to);
    const p = new URLSearchParams(searchParams);
    p.set("from", from); p.set("to", to);
    setSearchParams(p);
  };

  const doPrint = () => printFull();

  // Quick print scopes — set the filter first, wait a tick for the table
  // to re-render, then fire window.print() so the slim technician sheet
  // reflects the chosen scope.
  const printForTechOnly = () => {
    if (!filterEngineer || filterEngineer === "all") {
      toast.error("Pick a technician above first (or use the row action for a single record)");
      return;
    }
    printFull();
  };
  // Fetch the full filtered dataset (ignoring pagination) and run window.print()
  // once state updates have flushed. Covers "Print All", "Print by Region" and
  // "Print for Tech Only" — otherwise the browser would only print 25 rows.
  const printFull = async () => {
    try {
      const params = { size: 2000 };  // large enough for any realistic ledger
      if (filterRegion !== "all") params.region = filterRegion;
      if (filterStatus !== "all") params.status = filterStatus;
      if (filterEngineer !== "all") params.engineer_id = filterEngineer;
      if (filterCustomer !== "all") params.customer_id = filterCustomer;
      if (filterWalkin !== "all") params.walkin = filterWalkin;
      if (filterPl !== "exclude") params.pl = filterPl;
      if (filterSerial.trim()) params.serial = filterSerial.trim();
      if (filterSite.trim()) params.site = filterSite.trim();
      if (search.trim()) params.q = search.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      // No `page` → old array shape, server returns everything matching.
      const res = await api.get("/records", { params });
      const all = Array.isArray(res.data) ? res.data : (res.data.items || []);
      setPrintRecords(all);
      // Give React a tick to render the hidden print block with the full set.
      setTimeout(() => {
        window.print();
        // Clear so the on-screen tech-print block goes back to the paged view.
        setTimeout(() => setPrintRecords(null), 500);
      }, 100);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const printByRegion = () => {
    if (!filterRegion || filterRegion === "all") {
      toast.error("Pick a region above first");
      return;
    }
    printFull();
  };

  const doDelete = async (id) => {
    if (!window.confirm("Delete this record permanently?")) return;
    try {
      await api.delete(`/records/${id}`);
      toast.success("Record deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="border-b border-neutral-200 bg-white/80 backdrop-blur-xl sticky top-0 z-10 no-print">
        <div className="px-10 py-6 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="label-eyebrow">
              {filterStatus === "returned" ? "Return folder · RT" :
               filterStatus === "open"     ? "Issue folder · IV" :
                                              "Ledger · IV / RT"}
            </div>
            <h1 className="font-display text-4xl tracking-tight leading-none mt-2">
              {filterStatus === "returned" ? "Returned Parts" :
               filterStatus === "open"     ? "Issued Parts" :
                                              "Parts Records"}
            </h1>
            {filterStatus === "returned" && (
              <p className="text-xs text-neutral-500 mt-2 font-mono">
                RT vouchers · engineer on-hand deducted automatically on return.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="date-range-btn"
                  className={`h-10 rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-neutral-50 ${
                    (dateFrom || dateTo) ? "bg-neutral-900 text-neutral-50" : ""
                  }`}
                  title="Filter records by date range"
                >
                  <CalendarBlank size={16} weight="bold" className="mr-2" />
                  {dateRangeLabel || "Date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" sideOffset={4} className="w-[340px] p-0 rounded-none border border-neutral-900 shadow-lg" data-testid="date-range-popover">
                <div className="border-b border-neutral-200 px-4 py-2.5 flex items-center justify-between">
                  <div className="label-eyebrow">Filter by date</div>
                  {(dateFrom || dateTo) && (
                    <button
                      type="button"
                      onClick={clearDateRange}
                      data-testid="date-range-clear"
                      className="text-[10px] font-mono uppercase tracking-wider underline underline-offset-2 hover:text-red-700"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <div className="p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="label-eyebrow">From</Label>
                      <Input
                        type="date"
                        value={dateFrom}
                        max={dateTo || undefined}
                        onChange={(e) => { setDateFrom(e.target.value); }}
                        onBlur={commitDateRange}
                        className="h-9 rounded-none border-neutral-400 font-mono"
                        data-testid="date-from"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="label-eyebrow">To</Label>
                      <Input
                        type="date"
                        value={dateTo}
                        min={dateFrom || undefined}
                        onChange={(e) => { setDateTo(e.target.value); }}
                        onBlur={commitDateRange}
                        className="h-9 rounded-none border-neutral-400 font-mono"
                        data-testid="date-to"
                      />
                    </div>
                  </div>
                  <div className="pt-2 border-t border-neutral-100">
                    <div className="label-eyebrow mb-1.5">Quick ranges</div>
                    <div className="grid grid-cols-5 gap-1 text-[10px] font-mono uppercase tracking-wider">
                      {[
                        { k: "today", l: "Today" },
                        { k: "7d",    l: "7 days" },
                        { k: "30d",   l: "30 days" },
                        { k: "mtd",   l: "M-T-D" },
                        { k: "ytd",   l: "Y-T-D" },
                      ].map((p) => (
                        <button
                          key={p.k}
                          type="button"
                          onClick={() => applyPreset(p.k)}
                          data-testid={`date-preset-${p.k}`}
                          className="border border-neutral-300 py-1.5 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white"
                        >
                          {p.l}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            <Button
              variant="outline"
              onClick={printForTechOnly}
              data-testid="print-tech-btn"
              className="h-10 rounded-none border-emerald-700 text-emerald-700 hover:bg-emerald-700 hover:text-white"
              title="Print only rows for the technician selected in the filter"
            >
              <Printer size={16} weight="bold" className="mr-2" /> Print for Tech Only
            </Button>
            <Button
              variant="outline"
              onClick={printByRegion}
              data-testid="print-region-btn"
              className="h-10 rounded-none border-blue-700 text-blue-700 hover:bg-blue-700 hover:text-white"
              title="Print only rows for the region selected in the filter"
            >
              <Printer size={16} weight="bold" className="mr-2" /> Print by Region
            </Button>
            <Button
              variant="outline"
              onClick={doPrint}
              data-testid="print-btn"
              className="h-10 rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-neutral-50"
            >
              <Printer size={16} weight="bold" className="mr-2" /> Print All
            </Button>
            {canCreate && (
              <Button
                onClick={() => setShowCreate(true)}
                data-testid="new-record-btn"
                className="h-10 rounded-none bg-neutral-900 text-neutral-50 hover:bg-neutral-800"
              >
                <Plus size={16} weight="bold" className="mr-2" /> New Entry
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="px-10 pb-4 grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="filters">
          <Input
            placeholder="Search IV/RT/REQ, WO, engineer, part, region…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="filter-search"
            className="h-10 rounded-none border-neutral-300 focus-visible:border-neutral-900 focus-visible:ring-0"
          />
          <Select value={filterRegion} onValueChange={setFilterRegion}>
            <SelectTrigger data-testid="filter-region" className="h-10 rounded-none border-neutral-300"><SelectValue placeholder="Region" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Regions</SelectItem>
              {regions.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={changeStatus}>
            <SelectTrigger data-testid="filter-status" className="h-10 rounded-none border-neutral-300"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="returned">Returned</SelectItem>
            </SelectContent>
          </Select>
          {user.role !== "engineer" && (
            <Select value={filterEngineer} onValueChange={setFilterEngineer}>
              <SelectTrigger data-testid="filter-engineer" className="h-10 rounded-none border-neutral-300"><SelectValue placeholder="Engineer" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Engineers</SelectItem>
                {engineers.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {user.role !== "engineer" && (
            <Select value={filterCustomer} onValueChange={setFilterCustomer}>
              <SelectTrigger data-testid="filter-customer" className="h-10 rounded-none border-neutral-300"><SelectValue placeholder="Customer" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Customers</SelectItem>
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
        {/* Serial / Ref search — matches any serial recorded on a part
            (issue, return, or faulty). Case-insensitive, contains-match. */}
        <div className="px-4 pb-4 md:px-10 no-print" data-testid="serial-search-row">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 whitespace-nowrap">Serial / Ref</span>
            <Input
              placeholder="Find a part by its serial or reference number…"
              value={filterSerial}
              onChange={(e) => {
                const v = e.target.value;
                setFilterSerial(v);
                const p = new URLSearchParams(searchParams);
                if (v.trim()) p.set("serial", v.trim()); else p.delete("serial");
                setSearchParams(p, { replace: true });
              }}
              data-testid="filter-serial"
              className="h-9 rounded-none border-neutral-300 focus-visible:border-neutral-900 focus-visible:ring-0 font-mono max-w-md"
            />
            {filterSerial && (
              <button
                type="button"
                onClick={() => {
                  setFilterSerial("");
                  const p = new URLSearchParams(searchParams);
                  p.delete("serial");
                  setSearchParams(p, { replace: true });
                }}
                data-testid="filter-serial-clear"
                className="h-9 px-3 text-[11px] font-mono uppercase tracking-wider border border-neutral-300 bg-white hover:border-neutral-900 hover:bg-neutral-900 hover:text-white"
              >
                Clear
              </button>
            )}
          </div>
        </div>
        {/* Site search — filters records by their captured site name (contains-match). */}
        <div className="px-4 pb-4 md:px-10 no-print" data-testid="site-search-row">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 whitespace-nowrap">Site</span>
            <Input
              placeholder="Filter by site name (e.g. Petronas KLCC)…"
              value={filterSite}
              onChange={(e) => {
                const v = e.target.value;
                setFilterSite(v);
                const p = new URLSearchParams(searchParams);
                if (v.trim()) p.set("site", v.trim()); else p.delete("site");
                setSearchParams(p, { replace: true });
              }}
              data-testid="filter-site"
              className="h-9 rounded-none border-neutral-300 focus-visible:border-neutral-900 focus-visible:ring-0 max-w-md"
            />
            {filterSite && (
              <button
                type="button"
                onClick={() => {
                  setFilterSite("");
                  const p = new URLSearchParams(searchParams);
                  p.delete("site");
                  setSearchParams(p, { replace: true });
                }}
                data-testid="filter-site-clear"
                className="h-9 px-3 text-[11px] font-mono uppercase tracking-wider border border-neutral-300 bg-white hover:border-neutral-900 hover:bg-neutral-900 hover:text-white"
              >
                Clear
              </button>
            )}
          </div>
        </div>
        {/* Walk-in filter chips — keeps month-end engineer reports clean */}
        {user.role !== "engineer" && (
          <div className="px-4 pb-4 md:px-10 flex items-center gap-4 no-print flex-wrap" data-testid="ledger-chip-row">
            <div className="flex items-center gap-2" data-testid="walkin-chip-row">
              <span className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 mr-1">Walk-in</span>
              {[
                { key: "all",     label: "Show all",   testid: "walkin-chip-all" },
                { key: "exclude", label: "Hide walk-ins", testid: "walkin-chip-exclude" },
                { key: "only",    label: "Walk-ins only", testid: "walkin-chip-only" },
              ].map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => changeWalkin(c.key)}
                  data-testid={c.testid}
                  className={`h-7 px-3 text-[11px] font-mono uppercase tracking-wider border transition-colors ${
                    filterWalkin === c.key
                      ? "border-emerald-700 bg-emerald-700 text-white"
                      : "border-neutral-300 bg-white text-neutral-700 hover:border-emerald-700 hover:text-emerald-800"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2" data-testid="pl-chip-row">
              <span className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 mr-1">Packing lists</span>
              {[
                { key: "exclude", label: "Exclude PLs", testid: "pl-chip-exclude" },
                { key: "include", label: "Include PLs", testid: "pl-chip-include" },
                { key: "only",    label: "PLs only",    testid: "pl-chip-only" },
              ].map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => changePl(c.key)}
                  data-testid={c.testid}
                  className={`h-7 px-3 text-[11px] font-mono uppercase tracking-wider border transition-colors ${
                    filterPl === c.key
                      ? "border-teal-700 bg-teal-700 text-white"
                      : "border-neutral-300 bg-white text-neutral-700 hover:border-teal-700 hover:text-teal-800"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Print header - only visible when printing */}
      <div className="hidden print:block px-10 py-6">
        <h1 className="text-2xl font-bold">Parts Ledger — {nowStamp()}</h1>
        <div className="text-sm">Filters: region={filterRegion}, status={filterStatus}, engineer={filterEngineer}</div>
      </div>

      {/* Slim technician-friendly print — groups by engineer, 10 rows / page */}
      <TechnicianPrintSheets records={printRecords || filtered} />

      {/* Table */}
      <div className="px-10 py-6 print-full no-print">
        <div className="border border-neutral-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="records-table">
            <thead className="border-b border-neutral-200 bg-neutral-50">
              <tr className="text-left">
                <th className="px-3 py-3 label-eyebrow">Receipt #</th>
                <th className="px-3 py-3 label-eyebrow">Engineer</th>
                <th className="px-3 py-3 label-eyebrow">Region</th>
                <th className="px-3 py-3 label-eyebrow">Customer</th>
                <th className="px-3 py-3 label-eyebrow">Work Order</th>
                <th className="px-3 py-3 label-eyebrow">Site</th>
                <th className="px-3 py-3 label-eyebrow">Parts Taken</th>
                <th className="px-3 py-3 label-eyebrow">Taken At</th>
                <th className="px-3 py-3 label-eyebrow">Parts Returned</th>
                <th className="px-3 py-3 label-eyebrow">Returned At</th>
                <th className="px-3 py-3 label-eyebrow">Status</th>
                <th className="px-3 py-3 label-eyebrow no-print">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={11} className="px-4 py-10 text-center text-neutral-400">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={11} className="px-4 py-10 text-center text-neutral-400">No records match your filters.</td></tr>
              )}
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-neutral-100 last:border-0 align-top hover:bg-neutral-50/60" data-testid={`record-row-${r.id}`}>
                  <td className="px-3 py-3 font-mono text-xs whitespace-nowrap" data-testid={`receipt-no-${r.id}`}>
                    {r.receipt_no
                      ? <span className="font-semibold text-neutral-900">{r.receipt_no}</span>
                      : <span className="text-neutral-400">—</span>}
                    {r.is_walkin && (
                      <div className="mt-0.5">
                        <span
                          className="inline-block px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider border border-emerald-700 bg-emerald-50 text-emerald-800"
                          data-testid={`walkin-badge-${r.id}`}
                          title={r.walkin_total ? `Cash sale · RM ${r.walkin_total.toFixed(2)}` : "Walk-in cash sale"}
                        >
                          Walk-in{r.walkin_total ? ` · RM ${r.walkin_total.toFixed(2)}` : ""}
                        </span>
                        {r.payment_method && (
                          <span
                            className="ml-1 inline-block px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider border border-neutral-400 bg-neutral-50 text-neutral-700"
                            data-testid={`walkin-pay-badge-${r.id}`}
                            title={r.payment_ref ? `${r.payment_method} · ${r.payment_ref}` : r.payment_method}
                          >
                            {r.payment_method}{r.payment_ref ? ` ${r.payment_ref}` : ""}
                          </span>
                        )}
                      </div>
                    )}
                    {r.is_pl && (
                      <div className="mt-0.5">
                        <span
                          className="inline-block px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider border border-teal-700 bg-teal-50 text-teal-800"
                          data-testid={`pl-badge-${r.id}`}
                          title="Packing List — shipping doc"
                        >
                          Packing List
                        </span>
                      </div>
                    )}
                    {r.is_do && (
                      <div className="mt-0.5">
                        <span
                          className="inline-block px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider border border-emerald-700 bg-emerald-50 text-emerald-800"
                          data-testid={`do-badge-${r.id}`}
                          title={`Delivery Order — status: ${r.do_status || "?"}`}
                        >
                          DO · {r.do_status || "?"}
                        </span>
                      </div>
                    )}
                    {r.is_po && (
                      <div className="mt-0.5">
                        <span
                          className="inline-block px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider border border-blue-700 bg-blue-50 text-blue-800"
                          data-testid={`po-badge-${r.id}`}
                          title={`Purchase Order — status: ${r.po_status || "?"}`}
                        >
                          PO · {r.po_status || "?"}
                        </span>
                      </div>
                    )}
                    {r.return_receipt_no && (
                      <div className="text-emerald-700 mt-0.5" data-testid={`return-receipt-no-${r.id}`}>{r.return_receipt_no}</div>
                    )}
                    {r.request_no && (
                      <div className="text-indigo-700 mt-0.5" data-testid={`linked-req-no-${r.id}`}>← {r.request_no}</div>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <div className="font-medium">{r.engineer_name}</div>
                    {r.engineer_email && <div className="text-xs text-neutral-500 font-mono">{r.engineer_email}</div>}
                  </td>
                  <td className="px-3 py-3 font-mono text-xs">{r.region}</td>
                  <td className="px-3 py-3 text-xs">
                    {r.customer_name
                      ? <span className="font-medium">{r.customer_name}</span>
                      : <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="px-3 py-3 font-mono text-xs">
                    {r.work_order_id ? (
                      <a href={`/lookup?id=${encodeURIComponent(r.work_order_id)}`} className="underline underline-offset-2 hover:text-neutral-500">
                        {r.work_order_id}
                      </a>
                    ) : <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="px-3 py-3 text-xs" data-testid={`site-cell-${r.id}`}>
                    {r.site ? <span className="font-medium">{r.site}</span> : <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="px-3 py-3">
                    <ul className="space-y-1">
                      {r.parts_taken.map((p, i) => (
                        <li key={i} className="text-xs">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-medium">{p.name}</span>
                            <span className="text-neutral-500">× {p.quantity}</span>
                            <KindBadge kind={p.kind} />
                            {p.chargeable && (
                              <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider bg-amber-100 text-amber-900">Chargeable</span>
                            )}
                            <GroupBadge group={p.group} />
                          </div>
                          {p.serials && p.serials.length > 0 && (
                            <div className="mt-0.5 flex flex-wrap gap-1" data-testid={`row-serials-${r.id}-${i}`}>
                              {p.serials.map((s, si) => {
                                const hit = filterSerial.trim() && s.toLowerCase().includes(filterSerial.trim().toLowerCase());
                                return (
                                  <span
                                    key={si}
                                    className={`px-1.5 py-0.5 text-[10px] font-mono border ${hit ? "border-amber-600 bg-amber-100 text-amber-900" : "border-neutral-300 bg-neutral-50 text-neutral-700"}`}
                                    title="Serial / reference number"
                                  >
                                    {s}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td className="px-3 py-3 font-mono text-xs whitespace-nowrap">{fmtDateTime(r.taken_at)}</td>
                  <td className="px-3 py-3">
                    {r.parts_returned.length === 0 ? (
                      <span className="text-xs text-neutral-400">—</span>
                    ) : (
                      <ul className="space-y-1">
                        {r.parts_returned.map((p, i) => (
                          <li key={i} className="text-xs flex items-center gap-1.5 flex-wrap">
                            <span className="font-medium">{p.name}</span>
                            <span className="text-neutral-500">× {p.quantity}</span>
                            <KindBadge kind={p.kind} />
                            <GroupBadge group={p.group} />
                            <span className={`px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider ${p.status === "used" ? "bg-red-100 text-red-800" : "bg-emerald-100 text-emerald-800"}`}>
                              {p.status}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td className="px-3 py-3 font-mono text-xs whitespace-nowrap">{r.returned_at ? fmtDateTime(r.returned_at) : "—"}</td>
                  <td className="px-3 py-3">
                    <span className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${r.status === "open" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-3 py-3 no-print">
                    <div className="flex gap-1">
                      <button
                        onClick={() => setDetailId(r.id)}
                        data-testid={`view-btn-${r.id}`}
                        className="p-1.5 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white"
                        title="View details"
                      >
                        <Eye size={14} weight="bold" />
                      </button>
                      <button
                        onClick={() => downloadReceipt(r.id)}
                        data-testid={`receipt-btn-${r.id}`}
                        className="p-1.5 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white"
                        title="Download PDF receipt"
                      >
                        <FilePdf size={14} weight="bold" />
                      </button>
                      <button
                        onClick={() => downloadReceipt(r.id, 3)}
                        data-testid={`receipt-3ply-btn-${r.id}`}
                        className="p-1.5 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white relative"
                        title="Print 3 Copies (Original / Office / Technician)"
                      >
                        <FilePdf size={14} weight="bold" />
                        <span className="absolute -bottom-1 -right-1 bg-neutral-900 text-white text-[8px] font-mono px-1 leading-tight">3×</span>
                      </button>
                      {canReturnOnRecord(r) && r.status === "open" && (r.return_stage === "none" || !r.return_stage) && (
                        <button
                          onClick={() => { setActiveRecord(r); setShowReturn(true); }}
                          data-testid={`return-btn-${r.id}`}
                          className="p-1.5 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white"
                          title="Submit return request (Admin → Supervisor → Storekeeper)"
                        >
                          <ArrowClockwise size={14} weight="bold" />
                        </button>
                      )}
                      {canReturnOnRecord(r) && r.status === "open" && (r.return_stage === "pending_approval" || r.return_stage === "approved") && (
                        <span
                          className={`px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider ${
                            r.return_stage === "pending_approval" ? "bg-amber-100 text-amber-900" : "bg-emerald-100 text-emerald-900"
                          }`}
                          data-testid={`return-stage-badge-${r.id}`}
                          title="See Return Approvals"
                        >
                          {r.return_stage === "pending_approval" ? "Return · Pending" : "Return · Approved"}
                        </span>
                      )}
                      {canEdit && (
                        <button
                          onClick={() => setEditing(r)}
                          data-testid={`edit-btn-${r.id}`}
                          className="p-1.5 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white"
                          title="Edit record (WO, site, region, notes)"
                        >
                          <PencilSimple size={14} weight="bold" />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => doDelete(r.id)}
                          data-testid={`delete-btn-${r.id}`}
                          className="p-1.5 border border-neutral-300 hover:border-red-600 hover:bg-red-600 hover:text-white"
                          title="Delete record"
                        >
                          <Trash size={14} weight="bold" />
                        </button>
                      )}
                      {user.role === "manager" && (
                        <button
                          onClick={() => setChargeEditing(r)}
                          data-testid={`edit-chargeable-btn-${r.id}`}
                          className="p-1.5 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white"
                          title="Edit chargeable / non-chargeable flags"
                        >
                          <CurrencyDollar size={14} weight="bold" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pager — server-side pagination. Hidden during print. */}
        <div className="px-4 md:px-10 pt-3 pb-6 flex items-center justify-between no-print" data-testid="records-pager">
          <div className="text-xs font-mono text-neutral-500">
            {total === 0
              ? "No matching records"
              : `${((page - 1) * PAGE_SIZE) + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total.toLocaleString()}`}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              data-testid="pager-prev"
              className="h-8 px-3 text-[11px] font-mono uppercase tracking-wider border border-neutral-300 bg-white hover:border-neutral-900 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ← Prev
            </button>
            <span className="text-[11px] font-mono text-neutral-600 tabular-nums">
              Page {page} / {Math.max(1, Math.ceil(total / PAGE_SIZE))}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => (p * PAGE_SIZE < total ? p + 1 : p))}
              disabled={page * PAGE_SIZE >= total || loading}
              data-testid="pager-next"
              className="h-8 px-3 text-[11px] font-mono uppercase tracking-wider border border-neutral-300 bg-white hover:border-neutral-900 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next →
            </button>
          </div>
        </div>
      </div>

      {canCreate && (
        <CreateRecordDialog
          open={showCreate}
          onClose={() => setShowCreate(false)}
          engineers={engineers}
          regions={regions}
          customers={customers}
          currentUser={user}
          onSaved={() => { setShowCreate(false); load(); }}
        />
      )}
      {canReturn && activeRecord && (
        <ReturnRecordDialog
          open={showReturn}
          onClose={() => { setShowReturn(false); setActiveRecord(null); }}
          record={activeRecord}
          onSaved={() => { setShowReturn(false); setActiveRecord(null); load(); }}
        />
      )}
      <RecordDetailDialog
        recordId={detailId}
        open={!!detailId}
        onClose={() => setDetailId(null)}
      />
      {editing && canEdit && (
        <EditRecordDialog
          record={editing}
          regions={regions}
          customers={customers}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {chargeEditing && user.role === "manager" && (
        <EditChargeableDialog
          record={chargeEditing}
          onClose={() => setChargeEditing(null)}
          onSaved={() => { setChargeEditing(null); load(); }}
        />
      )}
    </div>
  );
}


function EditChargeableDialog({ record, onClose, onSaved }) {
  const [lines, setLines] = useState(
    (record.parts_taken || []).map((p) => ({ ...p, dirty: false, prev: !!p.chargeable }))
  );
  const [busy, setBusy] = useState(false);
  const dirtyCount = lines.filter((l) => l.chargeable !== l.prev && !l.invoiced).length;

  const toggle = (i) => {
    setLines(lines.map((l, idx) => (idx === i && !l.invoiced ? { ...l, chargeable: !l.chargeable } : l)));
  };

  const save = async () => {
    setBusy(true);
    let ok = 0, fail = 0;
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      if (l.chargeable === l.prev) continue;
      if (l.invoiced) continue;
      try {
        await api.patch(`/records/${record.id}/parts_taken/${i}/chargeable`, { chargeable: !!l.chargeable });
        ok += 1;
      } catch (e) {
        fail += 1;
        toast.error(`${l.name}: ${formatApiError(e)}`);
      }
    }
    setBusy(false);
    if (ok) toast.success(`${ok} line${ok > 1 ? "s" : ""} updated`);
    if (ok && !fail) onSaved();
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg rounded-none border-neutral-900" data-testid="edit-chargeable-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl tracking-tight">Edit Chargeable Lines</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-[11px] text-neutral-500 font-mono">
            {record.receipt_no} · {record.engineer_name}
          </div>
          <div className="border border-neutral-200">
            <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] font-mono uppercase tracking-wider bg-neutral-50 border-b border-neutral-200 text-neutral-600">
              <div className="col-span-6">Part</div>
              <div className="col-span-2 text-right">Qty</div>
              <div className="col-span-4 text-center">Chargeable?</div>
            </div>
            {lines.map((l, i) => (
              <label
                key={i}
                data-testid={`chargeable-line-${i}`}
                className={`grid grid-cols-12 gap-2 px-3 py-2 items-center text-sm border-b border-neutral-100 last:border-0 ${l.invoiced ? "bg-neutral-50/60 cursor-not-allowed" : "hover:bg-neutral-50 cursor-pointer"}`}
              >
                <div className="col-span-6 min-w-0">
                  <div className="font-medium truncate">{l.name}</div>
                  {l.stock_code && <div className="text-[10px] font-mono text-neutral-500 truncate">{l.stock_code}</div>}
                  {l.invoiced && (
                    <div className="text-[10px] font-mono uppercase tracking-wider text-emerald-700 mt-0.5">
                      invoiced · {l.invoice_no || "—"} (revert first to edit)
                    </div>
                  )}
                </div>
                <div className="col-span-2 text-right font-mono">{l.quantity}</div>
                <div className="col-span-4 text-center">
                  <input
                    type="checkbox"
                    checked={!!l.chargeable}
                    disabled={!!l.invoiced}
                    onChange={() => toggle(i)}
                    className="h-4 w-4 accent-neutral-900 disabled:opacity-40"
                    data-testid={`chargeable-toggle-${i}`}
                  />
                </div>
              </label>
            ))}
          </div>
          <div className="text-[11px] text-neutral-500">
            {dirtyCount > 0
              ? `${dirtyCount} line${dirtyCount > 1 ? "s" : ""} will be updated on save.`
              : "Tick or untick a line's checkbox to change its status."}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Cancel</Button>
          <Button
            onClick={save}
            disabled={busy || dirtyCount === 0}
            data-testid="edit-chargeable-submit"
            className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? "Saving…" : `Save ${dirtyCount || ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function EditRecordDialog({ record, regions, customers, onClose, onSaved }) {
  const [workOrderId, setWorkOrderId] = useState(record.work_order_id || "");
  const [site, setSite] = useState(record.site || "");
  const [region, setRegion] = useState(record.region || "");
  const [customerId, setCustomerId] = useState(record.customer_id || "__none__");
  const [notes, setNotes] = useState(record.notes || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/records/${record.id}`, {
        work_order_id: workOrderId.trim(),
        site: site.trim(),
        region,
        customer_id: customerId === "__none__" ? "" : customerId,
        notes,
      });
      toast.success(`Updated ${record.receipt_no || record.id.slice(0, 8)}`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg rounded-none border-neutral-900" data-testid="edit-record-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">
            Edit {record.receipt_no || "Record"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="border border-neutral-200 bg-neutral-50 px-3 py-2 text-[11px] text-neutral-600">
            Only header fields are editable. The parts list is locked to preserve stock reconciliation — if the parts are wrong, delete the record and raise a fresh IV.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Work Order ID</Label>
              <Input value={workOrderId} onChange={(e) => setWorkOrderId(e.target.value)} className="rounded-none border-neutral-400 font-mono" data-testid="edit-wo" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Site</Label>
              <SiteAutocomplete value={site} onChange={setSite} customerId={customerId} data-testid="edit-site" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Region</Label>
              <Select value={region} onValueChange={setRegion}>
                <SelectTrigger data-testid="edit-region" className="rounded-none border-neutral-400"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {regions.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Customer</Label>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger data-testid="edit-customer" className="rounded-none border-neutral-400"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__"><span className="text-neutral-500 italic">— No customer —</span></SelectItem>
                  {(customers || []).filter((c) => c.active || c.id === record.customer_id).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="rounded-none border-neutral-400" data-testid="edit-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="edit-submit" className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800">
            {busy ? "Saving…" : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateRecordDialog({ open, onClose, engineers, regions, customers, currentUser, onSaved }) {
  const [engineerId, setEngineerId] = useState("");
  const [region, setRegion] = useState("");
  const [customerId, setCustomerId] = useState("__none__");
  const [workOrderId, setWorkOrderId] = useState("");
  const [site, setSite] = useState("");
  const [takenAt, setTakenAt] = useState(() => new Date().toISOString().slice(0, 16));
  const [notes, setNotes] = useState("");
  const [parts, setParts] = useState([{ name: "", quantity: 1, kind: "new", notes: "", stock_code: null, group: null, uom: null }]);
  const [busy, setBusy] = useState(false);
  const [override, setOverride] = useState(false);
  const [lockStatus, setLockStatus] = useState({ blocked: false });
  const isManager = currentUser?.role === "manager";

  useEffect(() => { setOverride(false); }, [engineerId]);

  const addPart = () => setParts([...parts, { name: "", quantity: 1, kind: "new", notes: "", stock_code: null, group: null, uom: null }]);
  const removePart = (i) => setParts(parts.filter((_, idx) => idx !== i));
  const setPart = (i, k, v) => setParts(parts.map((p, idx) => idx === i ? { ...p, [k]: v } : p));
  const pickPart = (i, picked) => setParts(parts.map((p, idx) => idx === i ? {
    ...p,
    name: picked.name,
    stock_code: picked.stock_code || null,
    group: picked.group || null,
    uom: picked.uom || null,
  } : p));

  const submit = async () => {
    if (!engineerId) return toast.error("Select engineer");
    if (!region) return toast.error("Select region");
    const cleaned = parts
      .filter((p) => (p.name || "").trim())
      .map((p) => ({
        name: p.name.trim(),
        quantity: Number(p.quantity) || 1,
        kind: p.kind === "rb" ? "rb" : "new",
        notes: p.notes || "",
        stock_code: p.stock_code || null,
        group: p.group || null,
        uom: p.uom || null,
      }));
    if (cleaned.length === 0) return toast.error("Add at least one part");
    setBusy(true);
    try {
      await api.post("/records", {
        engineer_id: engineerId,
        region,
        customer_id: customerId === "__none__" ? null : customerId,
        work_order_id: workOrderId.trim() || null,
        site: site.trim() || null,
        parts_taken: cleaned,
        taken_at: new Date(takenAt).toISOString(),
        notes,
        override: isManager && override,
      });
      toast.success("Record created");
      setEngineerId(""); setRegion(""); setCustomerId("__none__"); setNotes(""); setWorkOrderId(""); setSite("");
      setParts([{ name: "", quantity: 1, kind: "new", notes: "", stock_code: null, group: null, uom: null }]);
      setOverride(false);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl rounded-none border-neutral-900" data-testid="create-record-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">New Parts Entry</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Engineer</Label>
              <Select value={engineerId} onValueChange={setEngineerId}>
                <SelectTrigger data-testid="create-engineer" className="rounded-none border-neutral-400"><SelectValue placeholder="Select engineer" /></SelectTrigger>
                <SelectContent>
                  {engineers.length === 0 ? (
                    <div className="px-2 py-3 text-xs text-neutral-500">No engineers — add one from Technicians.</div>
                  ) : engineers.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Region</Label>
              <Select value={region} onValueChange={setRegion}>
                <SelectTrigger data-testid="create-region" className="rounded-none border-neutral-400"><SelectValue placeholder="Select region" /></SelectTrigger>
                <SelectContent>
                  {regions.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="label-eyebrow">Customer <span className="text-neutral-400 normal-case">(optional — enables monthly consumption tracking)</span></Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger data-testid="create-customer" className="rounded-none border-neutral-400"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__"><span className="text-neutral-500 italic">— No customer —</span></SelectItem>
                {(customers || []).filter((c) => c.active !== false).map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Work Order ID</Label>
              <Input
                placeholder="e.g. WO-2026-0421"
                value={workOrderId}
                onChange={(e) => setWorkOrderId(e.target.value)}
                className="rounded-none border-neutral-400 font-mono"
                data-testid="create-work-order-id"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Site (optional)</Label>
              <SiteAutocomplete
                placeholder="Customer / location"
                value={site}
                onChange={setSite}
                customerId={customerId}
                data-testid="create-site"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="label-eyebrow">Taken at</Label>
            <Input type="datetime-local" value={takenAt} onChange={(e) => setTakenAt(e.target.value)} className="rounded-none border-neutral-400" data-testid="create-taken-at" />
          </div>

          {engineerId && (
            <LockStatusBanner
              engineerId={engineerId}
              parts={parts}
              isManager={isManager}
              override={override}
              onOverrideChange={setOverride}
              onStatusChange={setLockStatus}
              testidPrefix="create-lock"
            />
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="label-eyebrow">Parts Taken</Label>
              <button type="button" onClick={addPart} data-testid="add-part-btn" className="text-xs font-medium underline underline-offset-4 hover:text-neutral-500">
                + Add part
              </button>
            </div>
            <div className="space-y-2">
              {parts.map((p, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-5" data-testid={`part-name-${i}`}>
                    <StockPartCombobox
                      value={p}
                      onChange={(picked) => pickPart(i, picked)}
                      testid={`part-picker-${i}`}
                      placeholder="Search master list…"
                    />
                  </div>
                  <Input
                    type="number" min={1}
                    placeholder="Qty"
                    value={p.quantity}
                    onChange={(e) => setPart(i, "quantity", e.target.value)}
                    className="col-span-2 rounded-none border-neutral-400 h-9"
                    data-testid={`part-qty-${i}`}
                  />
                  <Select value={p.kind} onValueChange={(v) => setPart(i, "kind", v)}>
                    <SelectTrigger data-testid={`part-kind-${i}`} className="col-span-2 rounded-none border-neutral-400 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="rb">RB (Repair Basis)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Notes"
                    value={p.notes}
                    onChange={(e) => setPart(i, "notes", e.target.value)}
                    className="col-span-2 rounded-none border-neutral-400 h-9"
                  />
                  <button type="button" onClick={() => removePart(i)} className="col-span-1 h-9 flex items-center justify-center border border-neutral-300 hover:border-red-600 hover:text-red-600">
                    <X size={14} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="label-eyebrow">Notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-none border-neutral-400" data-testid="create-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Cancel</Button>
          <Button onClick={submit} disabled={busy || lockStatus.blocked} data-testid="create-submit" className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed">
            {busy ? "Saving…" : "Create Entry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReturnRecordDialog({ open, onClose, record, onSaved }) {
  // The dialog now supports:
  //  · PARTIAL returns — qty per line can be < issued qty
  //  · SPLIT returns   — same part appears on multiple rows (e.g. 5 unused + 5 used)
  //  · TICK-BOX INCLUDE — untick a row to leave that part with the engineer
  //                       (record stays open for the remainder)
  // Client-side we compute outstanding per part-key so techs can't over-return.
  const [parts, setParts] = useState(record.parts_taken.map((p) => ({
    ...p,
    status: "unused",
    kind: p.kind === "rb" ? "rb" : "new",
    include: true,   // default: returning this line
  })));
  const [faulty, setFaulty] = useState([]);
  const [returnedAt, setReturnedAt] = useState(() => new Date().toISOString().slice(0, 16));
  const [returnNotes, setReturnNotes] = useState("");
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const sigRef = useRef(null);
  const fileRef = useRef(null);

  // Outstanding qty for THIS record, per part-key, before this submission.
  // Ignores anything already in `parts_returned`/`faulty_parts` — for a
  // fresh return it's simply the taken qty.
  const outstandingByKey = useMemo(() => {
    const keyOf = (p) => ((p.stock_code || p.name || "") + "").toLowerCase();
    const out = {};
    (record.parts_taken || []).forEach((p) => {
      out[keyOf(p)] = (out[keyOf(p)] || 0) + Number(p.quantity || 0);
    });
    (record.parts_returned || []).forEach((p) => {
      out[keyOf(p)] = (out[keyOf(p)] || 0) - Number(p.quantity || 0);
    });
    (record.faulty_parts || []).forEach((p) => {
      out[keyOf(p)] = (out[keyOf(p)] || 0) - Number(p.quantity || 0);
    });
    return out;
  }, [record]);
  const keyOfRow = (p) => ((p.stock_code || p.name || "") + "").toLowerCase();

  // Live sum-by-key across parts + faulty for the current form.
  // Unticked (include=false) rows are IGNORED — they stay with the engineer.
  const proposedByKey = useMemo(() => {
    const out = {};
    parts.forEach((p) => {
      if (!p.include) return;
      const k = keyOfRow(p);
      out[k] = (out[k] || 0) + Number(p.quantity || 0);
    });
    faulty.forEach((p) => {
      const k = keyOfRow(p);
      out[k] = (out[k] || 0) + Number(p.quantity || 0);
    });
    return out;
  }, [parts, faulty]);

  const overByKey = useMemo(() => {
    const over = {};
    Object.entries(proposedByKey).forEach(([k, v]) => {
      const cap = outstandingByKey[k] ?? Infinity;
      if (v > cap) over[k] = { proposed: v, cap };
    });
    return over;
  }, [proposedByKey, outstandingByKey]);

  const setPart = (i, k, v) => setParts(parts.map((p, idx) => idx === i ? { ...p, [k]: v } : p));
  const splitPart = (i) => {
    // Duplicate row `i` with the opposite status, qty 0 — tech fills in the split.
    // Split copies inherit the parent's include state.
    const src = parts[i];
    const copy = { ...src, status: src.status === "unused" ? "used" : "unused", quantity: 0, include: true };
    const next = [...parts];
    next.splice(i + 1, 0, copy);
    setParts(next);
  };
  const removePart = (i) => setParts(parts.filter((_, idx) => idx !== i));
  const addFaulty = () => setFaulty([...faulty, { name: "", quantity: 1, notes: "", stock_code: null, group: null, uom: null }]);
  const removeFaulty = (i) => setFaulty(faulty.filter((_, idx) => idx !== i));
  const setFaultyField = (i, k, v) => setFaulty(faulty.map((p, idx) => idx === i ? { ...p, [k]: v } : p));
  const pickFaulty = (i, picked) => setFaulty(faulty.map((p, idx) => idx === i ? {
    ...p,
    name: picked.name,
    stock_code: picked.stock_code || null,
    group: picked.group || null,
    uom: picked.uom || null,
  } : p));

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Photo must be under 4 MB");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result);
    reader.readAsDataURL(file);
  };

  const clearPhoto = () => {
    setPhoto(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async () => {
    // Block over-return client-side (backend also enforces this).
    const anyOver = Object.entries(overByKey);
    if (anyOver.length) {
      const [k, info] = anyOver[0];
      return toast.error(`Total for "${k}" is ${info.proposed} — exceeds outstanding ${info.cap}`);
    }
    const faultyClean = faulty
      .filter((p) => (p.name || "").trim())
      .map((p) => ({
        name: p.name.trim(),
        quantity: Number(p.quantity) || 1,
        notes: (p.notes || "").trim(),
        stock_code: p.stock_code || null,
        group: p.group || null,
        uom: p.uom || null,
      }));
    // Frontend guard: match backend rule — faulty parts need a fault note.
    const missingNote = faultyClean.find((p) => !p.notes);
    if (missingNote) {
      return toast.error(`Fault note required for "${missingNote.name}"`);
    }
    const returnLines = parts
      .filter((p) => p.include && Number(p.quantity) > 0)
      .map((p) => ({
        name: p.name,
        quantity: Number(p.quantity),
        status: p.status,
        kind: p.kind === "rb" ? "rb" : "new",
        notes: p.notes || "",
        stock_code: p.stock_code || null,
        group: p.group || null,
        uom: p.uom || null,
      }));
    if (returnLines.length === 0 && faultyClean.length === 0) {
      return toast.error("Tick at least one part with qty > 0, or add a faulty part.");
    }
    setBusy(true);
    try {
      await api.post(`/records/${record.id}/return-request`, {
        parts_returned: returnLines,
        faulty_parts: faultyClean,
        return_notes: returnNotes,
      });
      // Detect partial: any outstanding minus proposed still > 0
      const stillOutstanding = Object.entries(outstandingByKey).some(
        ([k, cap]) => (cap - (proposedByKey[k] || 0)) > 0
      );
      toast.success(
        stillOutstanding
          ? "Partial return submitted — record stays open for the remainder"
          : "Return request submitted — awaiting Admin/Supervisor approval"
      );
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl rounded-none border-neutral-900 max-h-[90vh] overflow-y-auto" data-testid="return-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Submit Return Request</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <div className="text-sm space-y-0.5">
            <div><span className="label-eyebrow">Engineer</span> <span className="ml-2 font-medium">{record.engineer_name}</span></div>
            <div><span className="label-eyebrow">Region</span> <span className="ml-2 font-mono text-xs">{record.region}</span></div>
            {record.work_order_id && (
              <div><span className="label-eyebrow">Work Order</span> <span className="ml-2 font-mono text-xs">{record.work_order_id}</span></div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Returned at</Label>
            <Input type="datetime-local" value={returnedAt} onChange={(e) => setReturnedAt(e.target.value)} className="rounded-none border-neutral-400" data-testid="return-timestamp" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="label-eyebrow">Mark each issued part</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setParts(parts.map((p) => ({ ...p, include: true })))}
                  data-testid="return-tick-all"
                  className="text-[10px] font-mono uppercase tracking-wider underline underline-offset-4 hover:text-neutral-500"
                >
                  Tick all
                </button>
                <button
                  type="button"
                  onClick={() => setParts(parts.map((p) => ({ ...p, include: false })))}
                  data-testid="return-untick-all"
                  className="text-[10px] font-mono uppercase tracking-wider underline underline-offset-4 hover:text-neutral-500"
                >
                  Untick all
                </button>
              </div>
            </div>
            <div className="text-[10px] font-mono text-neutral-500 border border-dashed border-neutral-300 px-3 py-2" data-testid="return-tick-summary">
              {(() => {
                const included = parts.filter((p) => p.include && Number(p.quantity) > 0).length;
                const excluded = parts.filter((p) => !p.include).length;
                return (
                  <>
                    <b className="text-emerald-800">Returning {included}</b> line{included === 1 ? "" : "s"}
                    {excluded > 0 && (
                      <> · <b className="text-neutral-700">Keeping {excluded} with engineer</b> (record stays open)</>
                    )}
                  </>
                );
              })()}
            </div>
            {parts.map((p, i) => {
              const key = keyOfRow(p);
              const outstanding = outstandingByKey[key] ?? 0;
              const proposed = proposedByKey[key] || 0;
              const isOver = !!overByKey[key];
              const included = !!p.include;
              return (
                <div
                  key={i}
                  className={`grid grid-cols-12 gap-2 items-center border p-2 transition-opacity ${
                    !included ? "opacity-50 bg-neutral-50 border-dashed border-neutral-300"
                    : isOver ? "border-red-500 bg-red-50/40"
                    : "border-neutral-200"
                  }`}
                  data-testid={`return-row-${i}`}
                >
                  <label
                    className="col-span-1 flex items-center justify-center cursor-pointer select-none"
                    title={included ? "Untick to leave this part with the engineer" : "Tick to include in this return"}
                  >
                    <input
                      type="checkbox"
                      checked={included}
                      onChange={(e) => setPart(i, "include", e.target.checked)}
                      data-testid={`return-include-${i}`}
                      className="h-5 w-5 accent-emerald-700 cursor-pointer"
                    />
                  </label>
                  <div className="col-span-3">
                    <div className="text-sm font-medium">{p.name}</div>
                    <div className="text-[10px] text-neutral-500 font-mono">
                      {included ? (
                        <>
                          Outstanding: {outstanding}
                          {proposed !== Number(p.quantity || 0) && (
                            <> · <span className={isOver ? "text-red-600 font-semibold" : ""}>using {proposed} across rows</span></>
                          )}
                        </>
                      ) : (
                        <span className="text-neutral-500 uppercase tracking-wider">Stays with engineer</span>
                      )}
                    </div>
                  </div>
                  <Input
                    type="number" min={0} max={outstanding}
                    value={p.quantity}
                    onChange={(e) => setPart(i, "quantity", e.target.value)}
                    disabled={!included}
                    className={`col-span-2 rounded-none h-9 ${isOver ? "border-red-500" : "border-neutral-400"} disabled:bg-neutral-100`}
                    data-testid={`return-qty-${i}`}
                  />
                  <Select value={p.kind} onValueChange={(v) => setPart(i, "kind", v)} disabled={!included}>
                    <SelectTrigger data-testid={`return-kind-${i}`} className="col-span-2 rounded-none border-neutral-400 h-9 disabled:bg-neutral-100"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="rb">RB (Repair Basis)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={p.status} onValueChange={(v) => setPart(i, "status", v)} disabled={!included}>
                    <SelectTrigger data-testid={`return-status-${i}`} className="col-span-2 rounded-none border-neutral-400 h-9 disabled:bg-neutral-100"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unused">Unused</SelectItem>
                      <SelectItem value="used">Faulty</SelectItem>
                    </SelectContent>
                  </Select>
                  <button
                    type="button" onClick={() => splitPart(i)}
                    data-testid={`return-split-${i}`}
                    title="Split into two rows (e.g. 5 unused + 5 used)"
                    disabled={!included}
                    className="col-span-1 h-9 flex items-center justify-center border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white font-mono text-[10px] uppercase tracking-wider disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    Split
                  </button>
                  <button
                    type="button" onClick={() => removePart(i)}
                    data-testid={`return-remove-${i}`}
                    disabled={parts.length === 1}
                    className="col-span-1 h-9 flex items-center justify-center border border-neutral-300 hover:border-red-600 hover:text-red-600 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <X size={14} weight="bold" />
                  </button>
                </div>
              );
            })}
          </div>

          {/* Faulty parts collected FROM the site */}
          <div className="space-y-2 border-t border-neutral-200 pt-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="label-eyebrow">Faulty parts brought back from site</Label>
                <div className="text-[10px] text-neutral-500 font-mono mt-0.5">Defective components removed from customer equipment.</div>
              </div>
              <button type="button" onClick={addFaulty} data-testid="add-faulty-btn" className="text-xs font-medium underline underline-offset-4 hover:text-neutral-500">
                + Add faulty part
              </button>
            </div>
            {faulty.length === 0 && (
              <div className="text-xs text-neutral-400 border border-dashed border-neutral-300 px-3 py-4 text-center">
                None. Click "Add faulty part" if the technician brought defective parts back.
              </div>
            )}
            {faulty.map((p, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-6" data-testid={`faulty-name-${i}`}>
                  <StockPartCombobox
                    value={p}
                    onChange={(picked) => pickFaulty(i, picked)}
                    testid={`faulty-picker-${i}`}
                    placeholder="Search faulty part…"
                  />
                </div>
                <Input
                  type="number" min={1}
                  placeholder="Qty"
                  value={p.quantity}
                  onChange={(e) => setFaultyField(i, "quantity", e.target.value)}
                  className="col-span-2 rounded-none border-neutral-400 h-9"
                  data-testid={`faulty-qty-${i}`}
                />
                <Input
                  placeholder="Fault reason (required)"
                  value={p.notes}
                  onChange={(e) => setFaultyField(i, "notes", e.target.value)}
                  className={`col-span-3 rounded-none h-9 ${(p.notes || "").trim() ? "border-neutral-400" : "border-red-500"}`}
                  data-testid={`faulty-notes-${i}`}
                />
                <button type="button" onClick={() => removeFaulty(i)} className="col-span-1 h-9 flex items-center justify-center border border-neutral-300 hover:border-red-600 hover:text-red-600">
                  <X size={14} weight="bold" />
                </button>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Engineer Signature</Label>
              <SignaturePad ref={sigRef} width={380} height={140} />
              <p className="text-[10px] text-neutral-500 font-mono">Sign inside the box using mouse, finger or stylus.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Return Photo</Label>
              {photo ? (
                <div className="relative border border-neutral-900 bg-white">
                  <img src={photo} alt="return preview" className="w-full h-[140px] object-contain" data-testid="return-photo-preview" />
                  <button type="button" onClick={clearPhoto} data-testid="return-photo-clear" className="absolute top-2 right-2 text-[10px] font-mono uppercase tracking-wider px-2 py-1 border border-neutral-300 bg-white hover:bg-red-600 hover:text-white">
                    Remove
                  </button>
                </div>
              ) : (
                <label className="block border-2 border-dashed border-neutral-300 hover:border-neutral-900 p-6 text-center cursor-pointer transition-colors" data-testid="return-photo-dropzone">
                  <Camera size={22} weight="duotone" className="mx-auto mb-1" />
                  <div className="text-xs font-medium">Add photo</div>
                  <div className="text-[10px] text-neutral-500 mt-0.5">Camera or gallery · up to 4 MB</div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={onPhoto}
                    className="hidden"
                    data-testid="return-photo-input"
                  />
                </label>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="label-eyebrow">Return notes</Label>
            <Textarea rows={2} value={returnNotes} onChange={(e) => setReturnNotes(e.target.value)} className="rounded-none border-neutral-400" data-testid="return-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || Object.keys(overByKey).length > 0}
            data-testid="return-submit"
            className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? "Submitting…" : (Object.keys(overByKey).length > 0 ? "Fix over-return first" : "Submit for Approval")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------------------------------------------------------------------------
// TechnicianPrintSheets
// Slim print-only view: groups the filtered records by technician,
// 10 lines per printed page, columns:
// Date · REQ # · IV # · Part · Qty · Work Order · Customer
// -------------------------------------------------------------------------
function TechnicianPrintSheets({ records }) {
  const grouped = useMemo(() => {
    // 1 line per (record × part) so each row is one physical part.
    const rows = [];
    (records || []).forEach((r) => {
      (r.parts_taken || []).forEach((p) => {
        rows.push({
          engineer_id: r.engineer_id,
          engineer_name: r.engineer_name,
          date: r.taken_at || r.created_at,
          req_no: r.request_no || "",
          iv_no: r.receipt_no || "",
          part: p.name,
          qty: p.quantity,
          work_order: r.work_order_id || "",
          site: r.site || "",
          customer: r.customer_name || "",
        });
      });
    });
    // Group by tech and split each into pages of 10.
    const byTech = new Map();
    rows.forEach((row) => {
      const key = row.engineer_id || row.engineer_name || "unknown";
      if (!byTech.has(key)) byTech.set(key, { name: row.engineer_name || "Unknown", rows: [] });
      byTech.get(key).rows.push(row);
    });
    return Array.from(byTech.values()).map(({ name, rows }) => {
      const pages = [];
      for (let i = 0; i < rows.length; i += 10) pages.push(rows.slice(i, i + 10));
      return { name, pages, total: rows.length };
    });
  }, [records]);

  if (!grouped.length) return null;

  return (
    <div className="print-only print-slim" data-testid="tech-print-sheets">
      {grouped.map((tech, tIdx) => (
        tech.pages.map((page, pIdx) => (
          <div
            key={`${tIdx}-${pIdx}`}
            className={`print-page px-10 py-6 ${pIdx === tech.pages.length - 1 && tIdx === grouped.length - 1 ? "" : ""}`}
            data-testid={`tech-print-page-${tIdx}-${pIdx}`}
          >
            <div className="flex items-baseline justify-between mb-3 border-b border-black pb-2">
              <div>
                <h2 className="text-lg font-bold">FlowFuel Sdn Bhd — Parts Ledger</h2>
                <div className="text-sm">Technician: <b>{tech.name}</b></div>
              </div>
              <div className="text-xs">
                Page {pIdx + 1} of {tech.pages.length} · {tech.total} line{tech.total === 1 ? "" : "s"} total<br />
                Printed {nowStamp()}
              </div>
            </div>
            <table className="w-full text-[11px]">
              <thead>
                <tr>
                  {["Date", "REQ #", "IV #", "Part", "Qty", "Work Order", "Site", "Customer"].map((h) => (
                    <th key={h} className="border border-black px-1.5 py-1 text-left bg-neutral-100">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {page.map((row, i) => (
                  <tr key={i}>
                    <td className="border border-black px-1.5 py-1 whitespace-nowrap">
                      {row.date ? fmtDate(row.date) : "—"}
                    </td>
                    <td className="border border-black px-1.5 py-1 font-mono">{row.req_no || "—"}</td>
                    <td className="border border-black px-1.5 py-1 font-mono">{row.iv_no || "—"}</td>
                    <td className="border border-black px-1.5 py-1">{row.part}</td>
                    <td className="border border-black px-1.5 py-1 text-right">{row.qty}</td>
                    <td className="border border-black px-1.5 py-1 font-mono">{row.work_order || "—"}</td>
                    <td className="border border-black px-1.5 py-1">{row.site || "—"}</td>
                    <td className="border border-black px-1.5 py-1">{row.customer || "—"}</td>
                  </tr>
                ))}
                {/* pad short pages with empty rows so all pages align */}
                {page.length < 10 && Array.from({ length: 10 - page.length }).map((_, i) => (
                  <tr key={`pad-${i}`}>
                    {Array.from({ length: 8 }).map((__, j) => (
                      <td key={j} className="border border-black px-1.5 py-1">&nbsp;</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-[10px] mt-3 border-t border-black pt-1 flex justify-between">
              <span>Signature (Technician): ______________________</span>
              <span>Signature (Storekeeper): ______________________</span>
            </div>
          </div>
        ))
      ))}
    </div>
  );
}

